import { useState } from "react";
import { toast } from "sonner";
import { Heart, Building2, Copy, Mail, Phone, Sparkles, ShieldCheck } from "lucide-react";
import { SITE, IMAGES } from "../data/site";
import { SectionEyebrow } from "../components/SectionEyebrow";
import { Link } from "react-router-dom";

const AMOUNTS = [
    { v: "50.000", label: "Un mes de meriendas para 5 niños" },
    { v: "150.000", label: "Materiales de arte para 1 taller" },
    { v: "300.000", label: "Apoyo logístico de 1 evento" },
    { v: "Otro", label: "Define tu propio aporte" },
];

export default function Donar() {
    const [selected, setSelected] = useState(AMOUNTS[1]);
    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copiado al portapapeles");
    };
    return (
        <>
            <section className="topo-bg py-20 lg:py-28" data-testid="donate-hero">
                <div className="container-fe grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <SectionEyebrow>Donar</SectionEyebrow>
                        <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-navy leading-[1.05]">
                            Tu aporte se convierte en <span className="text-brand-green">oportunidad</span>.
                        </h1>
                        <p className="mt-7 text-lg text-muted-foreground leading-relaxed">
                            En Formar Estructura cada peso tiene un destino claro y trazable. El 87% de
                            nuestros recursos va directamente a programas. Tu donación abre puertas reales
                            para niños, jóvenes y familias del Tolima.
                        </p>
                        <div className="mt-7 flex items-center gap-3 text-sm text-brand-navy">
                            <ShieldCheck size={20} className="text-brand-green" />
                            <span>Reconocidos por el programa Aflora — Bolívar Davivienda</span>
                        </div>
                    </div>
                    <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white">
                        <img src={IMAGES.child_gift} alt="Niño beneficiario" className="w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            {/* Suggested amounts */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-fe">
                    <div className="max-w-2xl">
                        <SectionEyebrow>Aportes sugeridos</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl text-brand-navy leading-tight">
                            Cada monto tiene un impacto concreto.
                        </h2>
                    </div>
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {AMOUNTS.map((a) => (
                            <button
                                key={a.v}
                                type="button"
                                onClick={() => setSelected(a)}
                                data-testid={`donate-amount-${a.v}`}
                                className={`text-left rounded-2xl p-7 transition-all ${
                                    selected.v === a.v
                                        ? "bg-brand-green text-white shadow-xl shadow-brand-green/25 -translate-y-1"
                                        : "bg-white border border-black/5 hover:border-brand-green/40 hover:-translate-y-0.5"
                                }`}
                            >
                                <div className={`text-xs uppercase tracking-wider font-bold ${selected.v === a.v ? "text-white/80" : "text-brand-green"}`}>
                                    Aporte
                                </div>
                                <div className="font-display font-bold text-3xl mt-2">
                                    {a.v === "Otro" ? "Otro" : `$${a.v}`}
                                </div>
                                <div className={`mt-3 text-sm leading-relaxed ${selected.v === a.v ? "text-white/85" : "text-muted-foreground"}`}>
                                    {a.label}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Bank details */}
            <section className="py-20 lg:py-28 bg-brand-cream" data-testid="donate-bank-section">
                <div className="container-fe grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <SectionEyebrow>Cuenta bancaria</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl text-brand-navy leading-tight">
                            Donación por transferencia o consignación.
                        </h2>
                        <p className="mt-6 text-muted-foreground leading-relaxed text-lg">
                            Realiza tu aporte directamente a nuestra cuenta institucional. Una vez hecho
                            el pago, escríbenos a {" "}
                            <a href={`mailto:${SITE.email}`} className="text-brand-green font-semibold hover:underline">
                                {SITE.email}
                            </a>{" "}
                            con el comprobante para emitirte el certificado correspondiente.
                        </p>
                        <div className="mt-8 space-y-3">
                            {[
                                { id: "deductible", i: <Sparkles size={16} />, t: "Donación 100% deducible (consulta tu certificación)" },
                                { id: "trace", i: <ShieldCheck size={16} />, t: "Trazabilidad total: te informamos el destino del aporte" },
                                { id: "recurring", i: <Heart size={16} />, t: "Aportes recurrentes disponibles bajo solicitud" },
                            ].map((x) => (
                                <div key={x.id} className="flex items-center gap-3 text-sm text-brand-navy">
                                    <span className="h-9 w-9 rounded-full bg-white text-brand-green flex items-center justify-center">{x.i}</span>
                                    {x.t}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-3xl bg-white border border-black/5 p-8 lg:p-10 shadow-xl">
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-xl bg-brand-green text-white flex items-center justify-center">
                                <Building2 size={24} />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider font-bold text-brand-green">Datos bancarios</div>
                                <div className="font-display font-bold text-brand-navy text-lg">{SITE.bank.bankName}</div>
                            </div>
                        </div>
                        <div className="mt-7 space-y-4">
                            <BankRow label="Beneficiario" value={SITE.bank.beneficiary} onCopy={copy} />
                            <BankRow label="Tipo de cuenta" value={SITE.bank.accountType} onCopy={copy} />
                            <BankRow label="Número de cuenta" value={SITE.bank.accountNumber} onCopy={copy} hint />
                            <BankRow label="NIT" value={SITE.bank.nit} onCopy={copy} hint />
                        </div>

                        <div className="mt-8 pt-8 border-t border-black/5">
                            <div className="text-xs uppercase tracking-wider font-bold text-brand-green mb-3">Confirmación</div>
                            <div className="space-y-2.5">
                                <a href={`mailto:${SITE.email}`} className="flex items-center gap-2.5 text-sm text-brand-navy hover:text-brand-green" data-testid="donate-confirm-email">
                                    <Mail size={16} className="text-brand-green" /> {SITE.email}
                                </a>
                                <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-2.5 text-sm text-brand-navy hover:text-brand-green" data-testid="donate-confirm-phone">
                                    <Phone size={16} className="text-brand-green" /> {SITE.phone}
                                </a>
                            </div>
                        </div>

                        <Link to="/contacto" className="btn-primary-pill mt-8 w-full" data-testid="donate-cta-contact">
                            <Heart size={16} className="fill-white" /> Coordinar mi donación
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}

const BankRow = ({ label, value, onCopy, hint }) => (
    <div className="rounded-xl bg-brand-cream p-4 border border-black/5">
        <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
                <div className="font-medium text-brand-navy mt-1 break-words">{value}</div>
            </div>
            {!hint && (
                <button
                    type="button"
                    onClick={() => onCopy(value)}
                    className="h-9 w-9 rounded-full bg-white text-brand-navy hover:bg-brand-green hover:text-white flex items-center justify-center transition-colors shrink-0"
                    aria-label={`Copiar ${label}`}
                    data-testid={`bank-copy-${label}`}
                >
                    <Copy size={14} />
                </button>
            )}
        </div>
    </div>
);
